import React, { useCallback, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { Collapse } from '@material-ui/core';
import compact from 'lodash/compact';
import { Typography, Card, Callout, Button } from 'seagull';
import { WideArrowUpward, WideArrowDownward, Checkmark } from 'seagull/icons';
import { useGlobalContext, useIoContext, useStore, useStoreActions } from "../../../contexts";
import { selectors } from "../../../store/state-selectors";
import { KycCompletionStatus } from "../../../store/store.model";
import Brand from "./steps/Brand/Brand";
import TestAgreement from "./steps/TestAgreement/TestAgreement";
import ConnectBankAccount from "./steps/ConnectBankAccount/ConnectBankAccount";
import CompleteKYC from "./steps/CompleteKYC/CompleteKYC";
import SetupServices from "./steps/SetupServices";
import { CheckmarkWithSunCircle } from "./CheckmarkWithSunCircle";
import { ProgressBar } from "./ProgressBar/ProgressBar";
import styles from "./BusinessOnboardingWidget.module.scss";
import { getKycWarning } from "./BusinessOnboardingWidget.utils";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import { usePersona } from "../../../hooks/usePersona";
import TutorialVideos from "./steps/TutorialVideos/TutorialVideos";
import SetUpBrand from "./steps/SetUpBrand/SetUpBrand";
import { KYBProgressStatusEnum } from 'anchor-kyb-service/types';
export default function BusinessOnboardingWidget(props) {
  var _userInfo$worksAt;
  const {
    className
  } = props;
  const userInfo = useStore(state => state.userInfo);
  const businessClientData = useStore(selectors.user.businessClientData);
  const worksAt = useStore(selectors.user.business);
  const businessId = userInfo === null || userInfo === void 0 || (_userInfo$worksAt = userInfo.worksAt) === null || _userInfo$worksAt === void 0 ? void 0 : _userInfo$worksAt.id;
  const isMemberUser = useStore(selectors.user.isMemberUser);
  const videos = useStore(selectors.user.videos);
  const {
    api,
    logBI
  } = useStoreActions();
  const {
    openNewTab
  } = useIoContext();
  const {
    startLoader,
    stopLoader
  } = useGlobalContext();
  const persona = usePersona({
    onError: error => {
      stopLoader('persona-inquiry');
      console.error(error);
    },
    onReady: () => {
      stopLoader('persona-inquiry');
    },
    onComplete: _ref => {
      let {
        inquiryId,
        status
      } = _ref;
      console.log("Completed inquiry ".concat(inquiryId, " with status ").concat(status));
      getPersonaKYBStatus().catch(() => null).then(status => {
        if (status) setPersonaKYBStatus(status);
      });
    }
  });
  const {
    PERSONA_KYB = false,
    HOW_TO_VIDEOS,
    CUSTOM_BRANDING
  } = useFeatureFlags();
  const [personaKYBStatus, setPersonaKYBStatus] = useState(null);
  const getHostedKYBUrl = useCallback(() => {
    if (worksAt != null) {
      return api.vendor.getHostedKYBUrl(worksAt.id).then(res => res.data);
    }
  }, [worksAt]);
  const getPersonaKYBStatus = useCallback(async () => {
    if (worksAt != null) {
      return api.vendor.getKYBStatus(worksAt.id).then(res => res.status);
    }
  }, [worksAt]);
  useEffect(() => {
    const {
      kycCompletionStatus
    } = worksAt || {};
    if (kycCompletionStatus !== KycCompletionStatus.Completed) {
      getPersonaKYBStatus().catch(() => null).then(status => {
        if (status) setPersonaKYBStatus(status);
      });
    }
  }, [getPersonaKYBStatus, worksAt]);
  const startKyc = useCallback(async () => {
    logBI(11203);
    const {
      kycCompletionStatus
    } = worksAt || {};
    if (PERSONA_KYB && kycCompletionStatus === KycCompletionStatus.InitialIncomplete) {
      if (businessId != null) {
        startLoader('persona-inquiry');
        persona.open(businessId);
      }
    } else {
      const url = await getHostedKYBUrl();
      openNewTab(url);
    }
  }, [getHostedKYBUrl, PERSONA_KYB, worksAt, businessId, persona]);
  const {
    steps: allSteps,
    numCompleted,
    elementaryCompleted
  } = useMemo(() => {
    var _numSampleAgreements$, _numSampleAgreements$2;
    const {
      numSampleAgreements,
      hasLinkedBankAccount,
      kycCompletionStatus,
      logoUrl,
      branding
    } = worksAt || {};
    const totalSampleAgreements = ((_numSampleAgreements$ = numSampleAgreements === null || numSampleAgreements === void 0 ? void 0 : numSampleAgreements.approved) !== null && _numSampleAgreements$ !== void 0 ? _numSampleAgreements$ : 0) + ((_numSampleAgreements$2 = numSampleAgreements === null || numSampleAgreements === void 0 ? void 0 : numSampleAgreements.pending) !== null && _numSampleAgreements$2 !== void 0 ? _numSampleAgreements$2 : 0);
    const steps = compact([{
      id: 'brand',
      label: 'Set up your brand',
      complete: CUSTOM_BRANDING ? !!logoUrl : !!(logoUrl && branding !== null && branding !== void 0 && branding.color),
      minutes: 1,
      component: CUSTOM_BRANDING ? SetUpBrand : Brand
    }, {
      id: 'setupServices',
      label: 'Set up services',
      complete: !!(businessClientData !== null && businessClientData !== void 0 && businessClientData.modifiedAServiceTemplate),
      minutes: 2,
      component: SetupServices,
      hide: isMemberUser
    }, {
      id: 'testAgreement',
      label: 'Send a test agreement',
      complete: totalSampleAgreements > 0,
      minutes: 5,
      component: TestAgreement
    }, {
      id: 'connectBankAccount',
      label: 'Connect bank account',
      complete: !!hasLinkedBankAccount,
      minutes: 1,
      component: ConnectBankAccount
    }, {
      id: 'kyc',
      label: 'Verify your business identity',
      complete: kycCompletionStatus === KycCompletionStatus.Completed,
      minutes: 3,
      component: CompleteKYC,
      extraProps: {
        startKyc,
        isUnderReview: personaKYBStatus != null && [KYBProgressStatusEnum.AwaitingDecision, KYBProgressStatusEnum.Approved].includes(personaKYBStatus) && kycCompletionStatus != null && [KycCompletionStatus.InitialIncomplete, KycCompletionStatus.AwaitingCompletion].includes(kycCompletionStatus)
      }
    }, HOW_TO_VIDEOS && {
      id: 'tutorialVideos',
      label: 'Watch tutorial videos',
      complete: Object.values(videos).filter(watched => watched).length >= 4,
      component: TutorialVideos
    }]);
    return {
      steps,
      numCompleted: steps.filter(s => s.complete).length,
      elementaryCompleted: !!hasLinkedBankAccount && kycCompletionStatus === KycCompletionStatus.Completed
    };
  }, [startKyc, personaKYBStatus, worksAt, isMemberUser, businessClientData, HOW_TO_VIDEOS, CUSTOM_BRANDING]);
  const numSteps = allSteps.length;
  const allCompleted = numCompleted === numSteps;
  const kycWarning = getKycWarning(worksAt);
  const [expanded, setExpanded] = useState(!elementaryCompleted);
  const [currentStepIdx, setCurrentStepIdx] = useState(() => {
    const idx = allSteps.findIndex(s => !s.complete);
    return idx < 0 ? 0 : idx;
  });
  const StepComponent = allSteps[currentStepIdx].component;
  return /*#__PURE__*/React.createElement("div", {
    className: className
  }, kycWarning && /*#__PURE__*/React.createElement(Callout, {
    intent: "warning",
    className: styles.kycIncompleteBanner,
    "data-testid": kycWarning.testid,
    withAction: /*#__PURE__*/React.createElement(Button, {
      onClick: startKyc,
      skin: "darkSecondary"
    }, "Complete KYC verification")
  }, kycWarning.text), /*#__PURE__*/React.createElement(Card, {
    autoHeight: true
  }, /*#__PURE__*/React.createElement("button", {
    className: styles.toggle,
    "data-testid": "onboarding-widget-toggle-btn",
    onClick: () => setExpanded(!expanded)
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.titleWrapper
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "paragraph2",
    skin: allCompleted ? 'success' : 'standard',
    style: {
      display: 'flex',
      alignItems: 'center',
      gap: 8
    },
    "data-testid": "onboarding-widget-title"
  }, allCompleted && /*#__PURE__*/React.createElement("div", {
    className: styles.checkmark
  }, /*#__PURE__*/React.createElement(CheckmarkWithSunCircle, null)), allCompleted ? 'Setup completed' : "Complete your setup (".concat(numCompleted, "/").concat(numSteps, ")")), !allCompleted && /*#__PURE__*/React.createElement("div", {
    className: styles.progressBar
  }, /*#__PURE__*/React.createElement(ProgressBar, {
    total: numSteps,
    completed: numCompleted
  }))), /*#__PURE__*/React.createElement("div", {
    className: styles.toggleIcon
  }, expanded ? /*#__PURE__*/React.createElement(WideArrowUpward, null) : /*#__PURE__*/React.createElement(WideArrowDownward, null))), /*#__PURE__*/React.createElement(Collapse, {
    in: expanded
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.flexContainer,
    "data-testid": "onboarding-widget-expanded"
  }, /*#__PURE__*/React.createElement("div", null, allSteps.map((s, idx) => !s.hide && /*#__PURE__*/React.createElement(Typography, {
    key: s.id,
    as: "button",
    skin: currentStepIdx === idx ? 'standard' : 'secondary',
    className: cn(styles.stepBtn, {
      [styles.selected]: currentStepIdx === idx
    }),
    "data-testid": "onboarding-widget-".concat(s.id, "-option"),
    onClick: () => {
      if (currentStepIdx !== idx) {
        logBI(11200, {
          step: s.id
        });
      }
      setCurrentStepIdx(idx);
    }
  }, s.label, s.complete ? /*#__PURE__*/React.createElement(Typography, {
    as: "span",
    skin: "success",
    className: styles.stepBtnRight,
    "data-testid": "onboarding-widget-".concat(s.id, "-complete")
  }, /*#__PURE__*/React.createElement(Checkmark, null)) : /*#__PURE__*/React.createElement(Typography, {
    as: "span",
    variant: "paragraph3",
    skin: "disabled",
    className: styles.stepBtnRight,
    "data-testid": "onboarding-widget-".concat(s.id, "-duration")
  }, !!s.minutes && "".concat(s.minutes, " min"))))), /*#__PURE__*/React.createElement("div", {
    className: styles.stepBodyContainer
  }, /*#__PURE__*/React.createElement(StepComponent, {
    userInfo: userInfo,
    isComplete: allSteps[currentStepIdx].complete,
    extraProps: allSteps[currentStepIdx].extraProps
  }))))));
}